import { fromDate, type ZonedDateTime } from '@internationalized/date';
import clsx from 'clsx';
import * as React from 'react';
import {
    Calendar,
    CalendarCell,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHeader,
    CalendarHeaderCell,
    Heading,
} from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';

export type DatePickerProps = {
    /** The label of the picker. */
    label?: string;
    /** The date to display in the picker. */
    value: Date;
    /** Callback that is called when the date changes. */
    onChange: (date: Date) => void;
    /** Class name to apply to the component. */
    className?: string;
    /** Whether the picker is disabled. */
    disabled?: boolean;
};

/**
 * Component to render a calendar picker.
 */
export function DatePicker(props: DatePickerProps) {
    const { label, value, onChange, className, disabled = false } = props;

    const dateValue = React.useMemo(() => {
        return fromDate(value, 'UTC');
    }, [value]);

    const onDateChange = (date: ZonedDateTime) => {
        onChange(date.toDate());
    };

    return (
        <Calendar
            aria-label={label}
            isDisabled={disabled}
            value={dateValue}
            onChange={onDateChange}
            className={clsx('flex w-full flex-col gap-2', className)}
        >
            <header className="flex items-center gap-1 pr-0.5 pl-1">
                <Heading slot="title" className="flex-1 font-medium text-ui-action" />
                <div className="flex items-center gap-1">
                    <Button
                        slot="previous"
                        className="rounded p-1 hover:bg-action-tertiary"
                        leadingIcon={Icon.ChevronLeft}
                        iconOnly
                        kind="blank"
                        label="Previous month"
                    />
                    <Button
                        slot="next"
                        className="rounded p-1 hover:bg-action-tertiary"
                        leadingIcon={Icon.ChevronRight}
                        iconOnly
                        kind="blank"
                        label="Next month"
                    />
                </div>
            </header>
            <CalendarGrid className="w-full border-collapse [&_td]:py-0.5" weekdayStyle="short">
                <CalendarGridHeader>
                    {(day) => (
                        <CalendarHeaderCell className="text-light text-ui-small ">
                            {day.slice(0, 2)}
                        </CalendarHeaderCell>
                    )}
                </CalendarGridHeader>
                <CalendarGridBody>
                    {(date) => (
                        <CalendarCell
                            date={date}
                            className={clsx(
                                'm-0.5 flex size-8 items-center justify-center rounded-lg border border-transparent border-solid text-base text-ui-base',
                                'data-[outside-month]:text-light data-[selected]:text-white data-[disabled]:opacity-50 data-[focus-visible]:shadow-focus-ring',
                                'hover:bg-side data-[selected]:bg-primary hover:data-[selected]:cursor-default',
                                '[&[data-today]:not([data-selected])]:bg-standout-on-base'
                            )}
                        />
                    )}
                </CalendarGridBody>
            </CalendarGrid>
        </Calendar>
    );
}
